import {
  invitationInspectResponseSchema,
  loginResponseSchema,
  meResponseSchema,
  mfaSetupResponseSchema,
  mfaStatusSchema,
  recoveryCodesResponseSchema,
  type LoginRequest,
  type PlatformCapability,
} from '@avrahami/contracts';
import { fetchSession, type SessionState } from '@avrahami/ui';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export type { SessionState };

export const sessionQuery = queryOptions({
  queryKey: ['session'],
  queryFn: () => fetchSession(api),
  staleTime: 30_000,
  retry: false,
});

export function hasCapability(state: SessionState, capability: PlatformCapability): boolean {
  return (
    state.kind === 'signed-in' && (state.me.platform?.capabilities.includes(capability) ?? false)
  );
}

/**
 * The session changed in this tab (sign-in, second step, sign-out): nothing cached for the
 * previous user survives, and the session takes its new state. The session query itself is kept,
 * not removed, so the screens observing it see the change.
 */
export function sessionChanged(queryClient: QueryClient, state: SessionState): void {
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== sessionQuery.queryKey[0],
  });
  queryClient.setQueryData(sessionQuery.queryKey, state);
}

export const sessionApi = {
  login: (body: LoginRequest) => api.request('POST', '/v1/auth/login', loginResponseSchema, body),
  verifyMfa: (code: string) =>
    api.request('POST', '/v1/auth/mfa/verify', meResponseSchema, { code }),
  logout: () => api.send('POST', '/v1/auth/logout'),
  setupMfa: () => api.request('POST', '/v1/auth/mfa/setup', mfaSetupResponseSchema),
  enableMfa: (code: string) =>
    api.request('POST', '/v1/auth/mfa/enable', recoveryCodesResponseSchema, { code }),
  regenerateRecoveryCodes: (code: string) =>
    api.request('POST', '/v1/auth/mfa/recovery-codes', recoveryCodesResponseSchema, { code }),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api.send('POST', '/v1/auth/change-password', body),
};

export const mfaStatusQuery = queryOptions({
  queryKey: ['mfa-status'],
  queryFn: () => api.request('GET', '/v1/auth/mfa', mfaStatusSchema),
});

/** The public invitation page. */
export const invitationApi = {
  inspect: (token: string) =>
    api.request('POST', '/v1/auth/invitation/inspect', invitationInspectResponseSchema, { token }),
  accept: (token: string, password: string) =>
    api.send('POST', '/v1/auth/invitation/accept', { token, password }),
};

/** "Forgot password" (adr/0035): the link is mailed, then works like an invitation link. */
export const passwordResetApi = {
  request: (email: string) => api.send('POST', '/v1/auth/password-reset', { email }),
  inspect: (token: string) =>
    api.request('POST', '/v1/auth/password-reset/inspect', invitationInspectResponseSchema, {
      token,
    }),
  complete: (token: string, password: string) =>
    api.send('POST', '/v1/auth/password-reset/complete', { token, password }),
};
